import React, { useState } from "react";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { API, api } from "../lib/api";
import { FileText, FileSpreadsheet, ShieldCheck, Award, Construction, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

function Placeholder({ title, desc }) {
  return (
    <div className="glass rounded-lg p-12 text-center">
      <Construction className="w-12 h-12 mx-auto text-orange-400 mb-4" strokeWidth={2.5} />
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="text-gray-500 mt-2 max-w-md mx-auto">{desc}</p>
    </div>
  );
}

export function ReportsPage() {
  const { sites } = useSiteContext();
  const token = localStorage.getItem("f360_token");
  const [exporting, setExporting] = useState(null);

  const downloadPDF = async (siteId) => {
    const res = await fetch(`${API}/reports/pdf/${siteId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob));
  };

  const downloadExcel = async (site) => {
    setExporting(site.site_id);
    try {
      // Build multi-sheet workbook client-side from existing endpoints
      const [devices, alerts, telemetry] = await Promise.all([
        api.get("/devices", { params: { site_id: site.site_id } }),
        api.get("/alerts", { params: { site_id: site.site_id, limit: 200 } }),
        api.get(`/telemetry/${site.site_id}/history`, { params: { hours: 720 } }),
      ]);
      const tele = await api.get(`/telemetry/${site.site_id}`);

      const wb = XLSX.utils.book_new();

      const deviceSheet = XLSX.utils.json_to_sheet((devices.data || []).map((d) => ({
        "Device ID": d.device_id, "Device Name": d.device_name, "Type": d.device_type,
        "Make": d.make || "—", "Model": d.model || "—", "Zone": d.zone || "—",
        "Floor": d.floor_number || "—", "Status": d.status,
        "Last Seen": d.last_seen ? new Date(d.last_seen).toLocaleString("en-IN") : "—",
      })));
      XLSX.utils.book_append_sheet(wb, deviceSheet, "Devices");

      const teleSheet = XLSX.utils.json_to_sheet((telemetry.data || []).map((r) => ({
        "Timestamp": new Date(r.ts).toLocaleString("en-IN"),
        "Hydrant Pressure (Bar)": r.hydrant_pressure_bar,
        "Sprinkler Pressure (Bar)": r.sprinkler_pressure_bar,
        "UG Tank %": r.ug_tank_pct, "OHT %": r.oht_tank_pct, "Diesel %": r.diesel_tank_pct,
        "Main Hydrant Pump": r.main_hydrant_pump, "Standby Pump": r.standby_pump,
        "Jockey Pump": r.jockey_pump, "Main Sprinkler Pump": r.main_sprinkler_pump,
      })));
      XLSX.utils.book_append_sheet(wb, teleSheet, "Telemetry (30 Days)");

      const alertSheet = XLSX.utils.json_to_sheet((alerts.data || []).map((a) => ({
        "Severity": a.severity, "Title": a.title, "Description": a.message || "—",
        "Triggered": new Date(a.created_at).toLocaleString("en-IN"),
        "Status": a.status || (a.acknowledged ? "acknowledged" : "open"),
        "Acknowledged": a.acknowledged_at ? new Date(a.acknowledged_at).toLocaleString("en-IN") : "—",
        "Resolved": a.resolved_at ? new Date(a.resolved_at).toLocaleString("en-IN") : "—",
      })));
      XLSX.utils.book_append_sheet(wb, alertSheet, "Alerts Log");

      const t = tele.data || {};
      const compliance = [
        { Section: "A — Hydrant", Parameter: "Hydrant Riser Pressure", "GR Min": "≥3.5 Bar", "Live": `${(t.hydrant_pressure_bar || 0).toFixed(2)} Bar`, Status: (t.hydrant_pressure_bar || 0) >= 3.5 ? "COMPLIANT" : "CRITICAL" },
        { Section: "B — Tank", Parameter: "UG Tank Level", "GR Min": "≥50%", "Live": `${(t.ug_tank_pct || 0).toFixed(0)}%`, Status: (t.ug_tank_pct || 0) >= 50 ? "COMPLIANT" : "CRITICAL" },
        { Section: "B — Tank", Parameter: "OHT Level", "GR Min": "≥50%", "Live": `${(t.oht_tank_pct || 0).toFixed(0)}%`, Status: (t.oht_tank_pct || 0) >= 50 ? "COMPLIANT" : "CRITICAL" },
        { Section: "B — Tank", Parameter: "Diesel Day Tank", "GR Min": "≥20%", "Live": `${(t.diesel_tank_pct || 0).toFixed(0)}%`, Status: (t.diesel_tank_pct || 0) >= 20 ? "COMPLIANT" : "CRITICAL" },
        { Section: "C — Sprinkler", Parameter: "Sprinkler Riser Pressure", "GR Min": "≥5 Bar", "Live": `${(t.sprinkler_pressure_bar || 0).toFixed(2)} Bar`, Status: (t.sprinkler_pressure_bar || 0) >= 5 ? "COMPLIANT" : "CRITICAL" },
        { Section: "D — Detection", Parameter: "Fire Panels", "GR Min": "All active", "Live": `${t.detection_panels_active || 0}/${t.detection_panels_total || 0}`, Status: (t.detection_panels_active || 0) === (t.detection_panels_total || 1) ? "COMPLIANT" : "CRITICAL" },
        { Section: "E — Ventilation", Parameter: "Staircase Pressurization Fan", "GR Min": "running", "Live": t.staircase_pressurization_fan || "—", Status: t.staircase_pressurization_fan === "running" ? "COMPLIANT" : "CRITICAL" },
        { Section: "F — PA / Lighting", Parameter: "PA System", "GR Min": "working", "Live": t.pa_system || "—", Status: t.pa_system === "working" ? "COMPLIANT" : "CRITICAL" },
        { Section: "F — PA / Lighting", Parameter: "Emergency Lighting", "GR Min": "working", "Live": t.emergency_lighting || "—", Status: t.emergency_lighting === "working" ? "COMPLIANT" : "CRITICAL" },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(compliance), "GR Compliance");

      const filename = `Fire360Pro_${site.name.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Excel report downloaded: ${filename}`);
    } catch (e) {
      toast.error("Excel export failed: " + (e.message || ""));
    } finally {
      setExporting(null);
    }
  };

  const downloadMatrix = async () => {
    const res = await fetch(`${API}/reports/compliance-matrix/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "gr_compliance_matrix.pdf"; a.click();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Reports & Logs</h1>
          <p className="text-sm text-gray-500 mt-1">GR-compliant monthly reports, telemetry exports, audit logs</p>
        </div>
        <Button onClick={downloadMatrix} className="f360-gradient border-0" data-testid="matrix-pdf-btn">
          <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2.5} /> Download GR Compliance Matrix (PDF)
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sites.map((s) => (
          <div key={s.site_id} className="glass p-5 rounded-lg">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-400"><ShieldCheck className="w-3 h-3" strokeWidth={2.5} /> GR Compliant</div>
            <h3 className="text-lg font-bold mt-2">{s.name}</h3>
            <p className="text-xs text-gray-500">{s.address}</p>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => downloadPDF(s.site_id)} className="flex-1 f360-gradient border-0"><FileText className="w-4 h-4 mr-1" strokeWidth={2.5} />PDF</Button>
              <Button onClick={() => downloadExcel(s)} disabled={exporting === s.site_id} variant="outline" className="flex-1 border-gray-200" data-testid={`excel-${s.site_id}`}>
                {exporting === s.site_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" strokeWidth={2.5} /> : <FileSpreadsheet className="w-4 h-4 mr-1" strokeWidth={2.5} />}
                Excel
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HydrantPage() { return <Placeholder title="Hydrant System" desc="Detailed hydrant subsystem view with per-pump telemetry, run-hours, jockey cycling analytics. Live data is available in ACMS Live Monitor." />; }
export function SprinklerPage() { return <Placeholder title="Sprinkler System" desc="Sprinkler zone telemetry, zone valve state and GR 5-Bar riser compliance reports." />; }
export function TankPage() { return <Placeholder title="Fire Water Tank" desc="Tank-level historical analytics, refill events and ultrasonic sensor health." />; }
export function DetectionPage() { return <Placeholder title="Detection System" desc="Panel inventory, zone mapping and point-level alarm drilldown." />; }
export function VentilationPage() { return <Placeholder title="Ventilation System" desc="Staircase pressurization fans, smoke extraction, PA & emergency lighting." />; }
export function FloorsPage() { return <Placeholder title="Floor Plans" desc="Interactive floor-plan viewer with device overlays." />; }
export function RolesPage() { return <Placeholder title="Roles & Permissions" desc="Granular RBAC matrix — per-feature role grants." />; }
export function GatewayPage() { return <Placeholder title="IoT Gateway Settings" desc="MQTTS broker, data interval, UPS config, SIM status, firmware OTA." />; }
export function CertificatePage() { return (
  <div className="space-y-6">
    <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2"><Award className="w-7 h-7 text-orange-400"/> ACMS Certificate Generator</h1>
    <div className="glass p-8 rounded-lg f360-border-gradient">
      <div className="text-[10px] uppercase tracking-widest text-orange-400">Government Notification</div>
      <div className="text-2xl font-extrabold mt-2">GR No. MFS-2023/C.R.370/UD-14</div>
      <p className="text-gray-500 mt-3">Use the Reports page to download the official GR-compliant PDF per registered site. Certificate numbers, NOC details and pump/sprinkler pressure proofs are embedded.</p>
    </div>
  </div>
); }
export function ControllerPage() { return <Placeholder title="Controller Settings" desc="Auto/manual pump control, testing mode, scheduled drills." />; }
export function SettingsPage() { return <Placeholder title="System Settings" desc="Branding, notifications, data retention, backup schedules." />; }
